import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Container, Stack, Divider, Chip, Grid, IconButton } from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DiamondIcon from '@mui/icons-material/Diamond';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#020202',
        borderTop: '1px solid rgba(212, 175, 55, 0.2)', // Gold border
        py: 8,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(212, 175, 55, 0.05) 0%, transparent 60%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={6}>
          
          {/* Column 1: Brand & Identity */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3 }}>
              <img 
                src="/images/dlogo.png" 
                alt="Diamond Dollar" 
                style={{ 
                  height: '60px', 
                  objectFit: 'contain', 
                  mixBlendMode: 'screen', 
                  marginBottom: '20px' 
                }} 
              />
             
              
              {/* Trust Badges */}
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  icon={<VerifiedUserIcon sx={{ fontSize: '16px !important' }} />}
                  label=""
                  sx={{
                    bgcolor: 'rgba(212, 175, 55, 0.1)',
                    color: '#d4af37',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    borderRadius: '4px',
                  }}
                />
                <Chip
                  size="small"
                  icon={<DiamondIcon sx={{ fontSize: '16px !important' }} />}
                  label=""
                  sx={{
                    bgcolor: 'rgba(192, 192, 192, 0.1)',
                    color: '#c0c0c0',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    border: '1px solid rgba(192, 192, 192, 0.3)',
                    borderRadius: '4px',
                  }}
                />
              </Stack>
            </Box>
          </Grid>

          {/* Column 2: Navigation */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h6" sx={{ 
              color: '#ffffff', 
              fontFamily: "'Playfair Display', serif", 
              fontSize: '1.1rem', 
              mb: 3,
              letterSpacing: '1px'
            }}>
              Navigate
            </Typography>
            
            <Stack spacing={2}>
              {[
                { title: 'Home', path: '/' },
                { title: 'About', path: '/about' },
                { title: 'Technology', path: '/technology' },
                { title: 'Transparency', path: '/transparency' },
                { title: 'Developers', path: '/developers' },
                { title: 'FAQ', path: '/faq' },
              ].map((link) => (
                <Typography
                  key={link.title}
                  component={RouterLink}
                  to={link.path}
                  sx={{
                    color: '#a1a1a1',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': { 
                      color: '#d4af37',
                      transform: 'translateX(5px)'
                    },
                    '&::before': {
                      content: '""',
                      width: '6px',
                      height: '6px',
                      backgroundColor: '#d4af37',
                      borderRadius: '50%',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    },
                    '&:hover::before': {
                      opacity: 1,
                    }
                  }}
                >
                  {link.title}
                </Typography>
              ))}
            </Stack>
          </Grid>

          {/* Column 3: Legal & Disclaimer */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h6" sx={{ 
              color: '#ffffff', 
              fontFamily: "'Playfair Display', serif", 
              fontSize: '1.1rem', 
              mb: 3,
              letterSpacing: '1px'
            }}>
              Legal & Disclaimers
            </Typography>
            
            <Typography variant="body2" sx={{ color: '#777777', lineHeight: 1.8, fontSize: '0.75rem', mb: 2 }}>
              *Diamond Dollar (DD) is a digital asset 
            </Typography>
            
            

            {/* Address Section - US */}
            <Box sx={{ 
              pt: 3, 
              borderTop: '1px solid rgba(212, 175, 55, 0.1)',
            }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <PublicIcon sx={{ 
                    color: '#d4af37', 
                    fontSize: '20px',
                  }} />
                  <Typography variant="caption" sx={{ 
                    color: '#d4af37', 
                    fontSize: '0.55rem',
                    fontWeight: 700,
                    letterSpacing: '1px',
                  }}>
                    US
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ 
                    color: '#888888', 
                    fontSize: '0.7rem',
                    lineHeight: 1.6,
                    fontWeight: 600,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                  }}>
                    
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#a1a1a1', 
                    fontSize: '0.8rem',
                    lineHeight: 1.6,
                  }}>
                    1100 NW Northwood Road
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#a1a1a1', 
                    fontSize: '0.8rem',
                    lineHeight: 1.6,
                  }}>
                    Seattle, Washington State, 98177
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* WhatsApp Button */}
            <Box sx={{ 
              mt: 3, 
              pt: 2,
              borderTop: '1px solid rgba(212, 175, 55, 0.08)',
            }}>
              <IconButton
                component="a"
                href="https://wa.me/27720100332"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: '#25D366',
                  color: '#ffffff',
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#128C7E',
                    transform: 'scale(1.1)',
                    boxShadow: '0 0 20px rgba(37, 211, 102, 0.4)',
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '28px',
                  }
                }}
              >
                <WhatsAppIcon />
              </IconButton>
              <Typography variant="caption" sx={{ 
                display: 'block', 
                color: '#666666', 
                fontSize: '0.6rem',
                mt: 0.5,
                letterSpacing: '0.5px',
              }}>
                Chat on WhatsApp
              </Typography>
            </Box>
          </Grid>

        </Grid>

        {/* Bottom Section */}
        <Divider sx={{ borderColor: 'rgba(212, 175, 55, 0.15)', my: 6 }} />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={3}
        >
          <Box>
            <Typography variant="body2" sx={{ color: '#555555' }}>
              &copy; {new Date().getFullYear()} Diamond Dollar. All rights reserved.
            </Typography>
          </Box>

          <Stack direction="row" spacing={3} alignItems="center">
            <Typography
              component={RouterLink}
              to="/contact"
              sx={{
                color: '#777777',
                textDecoration: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                transition: 'color 0.3s ease',
                '&:hover': { color: '#d4af37' },
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              component={RouterLink}
              to="/contact"
              sx={{
                color: '#777777',
                textDecoration: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                transition: 'color 0.3s ease',
                '&:hover': { color: '#d4af37' },
              }}
            >
              Terms of Service
            </Typography>
          </Stack>
        </Stack>

      </Container>
    </Box>
  );
}

export default Footer;